import layout from './FancyTreeControl.html?raw';
import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import FancyTreeViewModel from '@/ViewModels/Controls/FancyTreeViewModel';

export default class FancyTreeControl extends Control {
    private static readonly controlname = 'fancytree';

    private jQueryControl: JQuery;
    private fancytree: Fancytree.Fancytree;
    // Referenz auf die Tabelle, um Fancytree/floatThead beim Dispose sauber abzubauen.
    private $table: JQuery;
    public getFancytree(): Fancytree.Fancytree {
        return this.fancytree;
    };

    // useable for executing code just after the fancytree is rendered i.e. the ft-container is attached to the DOM
    private widgetIsCreatedDeferred: JQueryDeferred<void> = $.Deferred();
    public widgetIsCreated: JQueryPromise<void> = this.widgetIsCreatedDeferred.promise();

    public context: FancyTreeViewModel;

    // injected
    public renderColumns: (event, data) => void;
    // injected
    public renderNode: (event, data) => void;
    // injected
    public postProcessNode: (node: Fancytree.FancytreeNode) => void;
    // injected
    public activate: (event, data) => void;
    // injected
    public select: (event, data) => void;
    // injected
    public deactivate: (event, data) => void;
    // injected
    public beforeExpand: (event, data) => boolean;
    // injected
    public expand: (event, data) => void;
    // injected
    public collapse: (event, data) => void;
    // injected
    public autoScroll: boolean = false;
    // injected
    public dblclick: (event, data) => boolean;
    // injected
    public click: (event, data) => boolean;

    public scrollParent: "fancytree-container" | "control-container" = "control-container";

    public constructor(private viewModel: FancyTreeViewModel, private element?: JQuery) {
        super(viewModel, element);

        let fancyTreeOptions: Fancytree.FancytreeOptions;
        fancyTreeOptions = {
            autoActivate: true,
            keyboard: true,
            debugLevel: 0,
            source: (event, data) => { return viewModel.nodes(); },
            extensions: viewModel.extensions,
            renderColumns: (event, data) => { if (this.renderColumns) this.renderColumns(event, data); },
            activate: (event, data) => { if (this.activate) this.activate(event, data); },
            dblclick: (event, data) => { return this.dblclick ? this.dblclick(event, data) : true; },
            click: (event, data) => { return this.click ? this.click(event, data) : true; },
            deactivate: (event, data) => { if (this.deactivate) this.deactivate(event, data); },
            createNode: (event, data) => { if (this.postProcessNode) this.postProcessNode(data.node); },
            lazyLoad: (event, data) => { if (viewModel.lazyLoad) viewModel.lazyLoad(event, data); },
            beforeExpand: (event, data) => { return this.beforeExpand ? this.beforeExpand(event, data) : true; },
            expand: (event, data) => {
                if (this.expand) {
                    this.expand(event, data);
                }

                let node = data.node;
                let expandedObservable = node.data["isExpandedObs"];
                if (expandedObservable) {
                    expandedObservable(true);
                }
            },
            collapse: (event, data) => {
                if (this.collapse) {
                    this.collapse(event, data);
                }

                let node = data.node;
                let expandedObservable = node.data["isExpandedObs"];
                if (expandedObservable) {
                    expandedObservable(false);
                }
            },
            autoScroll: viewModel.autoScroll === true,
            checkbox: viewModel.renderCheckboxes === true,
            icon: viewModel.renderIcons !== false,
            renderNode: (event, data) => { if (this.renderNode) this.renderNode(event, data); },
            select: (event, data) => { if (this.select) this.select(event, data); },
            nodata: false,
            escapeTitles: viewModel.escapeHtml  //SAGGRC-1276 >> https://github.com/mar10/fancytree/issues/1040
        };
        if (viewModel.selectMode) {
            fancyTreeOptions.selectMode = viewModel.selectMode;
        }


        if (_.contains(viewModel.extensions, "wide")) {
            fancyTreeOptions["wide"] = {
                iconWidth: "16px",     // Adjust this if @fancy-icon-width != "16px"
                iconSpacing: "3px",    // Adjust this if @fancy-icon-spacing != "3px"
                labelSpacing: "3px",   // Adjust this if padding between icon and label != "3px"
                levelOfs: "16px"       // Adjust this if ul padding != "16px"
            }
        }

        this.onWidgetCreated((widget) => {
            this.applyFancyTreeBindings();
            let $table = this.jQueryControl.children().find("table") as any;
            this.$table = $table;

            if (this.scrollParent === "control-container") {
                fancyTreeOptions.scrollParent = this.jQueryControl;
            }
            else {
                fancyTreeOptions.scrollParent = null; // runs into default -> fancytree-container
            }

            $table.fancytree(fancyTreeOptions);

            this.fancytree = $table.fancytree('getTree');
            this.syncExpandedStateWithObservable();

            this.applyBindings(this.jQueryControl, {
                hotkey: {
                    shortcut: 'space',
                    handler: (e) => {
                        let activeNode = $table.fancytree("getActiveNode");
                        if (activeNode.data.nodeViewModel)
                            activeNode.data.nodeViewModel.isChecked(!activeNode.data.nodeViewModel.isChecked());
                        e.stopPropagation();
                        e.preventDefault();
                    },
                    isGlobal: false
                },
                locator: this.viewModel.locator
            });

            if (fancyTreeOptions.extensions.indexOf("table") !== -1) {
                $table.floatThead({
                    zIndex: 99,
                    autoReflow: true,
                    scrollContainer: ($table) => {
                        return this.jQueryControl;
                    }
                });
            }

            this.defineSubscription(viewModel.activeNode, (value) => {
                if (value) {
                    this.fancytree.activateKey(value);
                }
            });

            this.defineSubscription(viewModel.nodes, (value) => {
                ko.cleanNode($table.find('tbody')[0]);
                $table.find('tbody').empty();
                this.fancytree.reload(value);
                if (this.postProcessNode) {
                    this.fancytree.visit(node => this.postProcessNode(node));
                }

                this.syncExpandedStateWithObservable();
            });

            this.widgetIsCreatedDeferred.resolve();
        });
    }

    private syncExpandedStateWithObservable() {
        this.fancytree.visit(node => {
            let expandedObservable: KnockoutObservable<boolean> = node.data["isExpandedObs"];

            if (expandedObservable) {
                let subscriptionCallback = this.defineSubscription(expandedObservable, (isExpanded) => {
                    let alreadyInSync = node.isExpanded() && isExpanded || !node.isExpanded() && !isExpanded;
                    if (alreadyInSync) {
                        return;
                    }

                    node.setExpanded(isExpanded, { noAnimation: true });
                });

                // Einmal initial aufrufen
                subscriptionCallback(expandedObservable());
            }
        });
    }

    private applyFancyTreeBindings() {
        let context = this.context;
        let identifier = this.identifier;

        let bindingParameters = {
            controlName: FancyTreeControl.controlname,
            loader: context.isBusy,
            controlIdent: identifier,
            invalid: context.invalidationText,
            visible: context.isVisible,
            styles: this.definePureComputed({
                read: () => {
                    return ['default'];
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    let states = [];
                    if (context.isRequired() === true) states.push('required');
                    if (context.isValid() === true) states.push('valid');
                    if (context.isValid() !== true) states.push('invalid');
                    if (context.isEnabled() === true) states.push('enabled');
                    if (context.isEnabled() !== true) states.push('disabled');

                    return states;
                }
            })
        }

        this.applyBindings(this.jQueryControl, bindingParameters);
    }

    /**
     * @override
     * Baut Fancytree und floatThead explizit ab. Ohne dieses Teardown halten die von
     * floatThead registrierten window-Handler die (nach dem Entfernen aus dem DOM)
     * detachte Tabelle samt Fancytree-Knotenbaum und deren node.data am Leben
     * (Memory-Leak).
     */
    protected disposeInternal(): void {
        this.destroyFancyTree();
        super.disposeInternal();
    }

    private destroyFancyTree(): void {
        const $table = this.$table;
        if ($table == null || $table.length === 0) {
            return;
        }

        // floatThead zuerst abbauen (window-Events), dann den Fancytree zerstören.
        try {
            if (typeof $table.floatThead === 'function') {
                $table.floatThead('destroy');
            }
        }
        catch (e) {
            // Teardown darf das Dispose nie verhindern.
        }

        try {
            $table.fancytree('destroy');
        }
        catch (e) {
            // Teardown darf das Dispose nie verhindern.
        }

        this.fancytree = null;
        this.$table = null;
    }

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div>');

        let timerId = this.applyTemplate(layout, this.jQueryControl);

        $.when(this.triggers[timerId]).then(() => {
            if (this.isDisposed)
                return;
            this.widget(null);
        });

        return this.jQueryControl;
    }
}

ComponentManager.setControl('FancyTree', FancyTreeControl, 'RADPresentation.HTMLClient.DefaultViewExtensions');